// Auth hook for Sunlight Grounds Care Client Portal
import { useState, useEffect, useCallback } from 'react';
import api from '@/api/client';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing auth on mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await api.login(email, password);
      
      if (response.token && response.user) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setToken(response.token);
        setUser(response.user);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: 'Invalid response from server' };
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      
      // Handle 403 - pending account
      if (error.response?.status === 403) {
        return { success: false, error: detail, isPending: true };
      }
      
      return { success: false, error: detail };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedProperty');
    localStorage.removeItem('api_mode');
    // Full page redirect to cleanly reset all React state, contexts, and polling
    window.location.replace('/login');
  }, []);

  const isAuthenticated = !!token && !!user;

  const hasTab = useCallback((tabId) => {
    if (!user?.allowed_tabs) return false;
    // Support checking multiple aliases for a tab
    const aliases = {
      'landscape_overview': ['landscape_overview', 'overview'],
      'landscape_schedule': ['landscape_schedule', 'schedule'],
      'landscape_photos': ['landscape_photos', 'photos'],
      'communication': ['communication', 'communications', 'messages'],
      'account': ['account', 'payments'],
    };
    const toCheck = aliases[tabId] || [tabId];
    return toCheck.some(id => user.allowed_tabs.includes(id));
  }, [user]);

  const getDefaultTab = useCallback(() => {
    if (!user?.allowed_tabs?.length) return '/overview';
    
    const tabRoutes = {
      'landscape_overview': '/overview',
      'landscape_schedule': '/schedule',
      'landscape_photos': '/delivery',
      'communication': '/comms'
    };
    
    const firstTab = user.allowed_tabs[0];
    return tabRoutes[firstTab] || '/overview';
  }, [user]);

  return {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated,
    hasTab,
    getDefaultTab
  };
}
